<template>
  <div v-if="!loading">
    <b-button
      v-if="!disableProposal"
      @click.stop="openModal"
      variant="primary"
      class="addBtn p-2 position-absolute"
    >
      <font-awesome-icon :icon="faPlusSquare" class="mr-1" />
      {{ $t("list.add") }}
    </b-button>
    <new-product-form
      :editProduct="editProduct"
      @addProduct="addNewProduct"
      @editProduct="editSelectedProduct"
      :showModal="showModal"
      @close="closeModal"
      :disableProposal="disableProposal"
    />
    <template v-if="products.length && !loading">
      <b-table
        ref="products"
        class="products-table"
        :items="products"
        :fields="visibleFields"
        borderless
        striped
        responsive
      >
        <template v-for="field in fields" :slot="`HEAD_${field.field}`">
          <list-table-header :key="field.field[0]" :field="field" />
        </template>
        <template v-for="field in fields" :slot="field.field" slot-scope="data">
          <span :key="field.field">
            {{ getField(data.item, field) }}
          </span>
        </template>
        <template slot="actions" slot-scope="data" v-if="!disableProposal">
          <div class="d-flex">
            <b-button
              variant="secondary"
              size="sm"
              class="px-1 text-dark bg-transparent border-0 text-uppercase"
              @click.stop.prevent="callEditItem(data.index)"
            >
              <font-awesome-icon :icon="faEdit" size="lg" />
            </b-button>
            <click-confirm
              class="
                btn btn-secondary
                text-dark
                bg-transparent
                border-0
                btn-sm
                px-1
                ml-1
                text-uppercase
              "
              :messages="confirmationMessages"
              yes-class="btn btn-success"
              no-class="btn btn-danger"
            >
              <template slot="confirm-yes-icon">
                <font-awesome-icon :icon="faThumbsUp" fixed-width />
              </template>
              <template slot="confirm-no-icon">
                <font-awesome-icon :icon="faThumbsDown" fixed-width />
              </template>

              <div @click.stop="removeSelectedProduct(data.index)">
                <font-awesome-icon :icon="faTrashAlt" fixed-width size="lg" />
              </div>
            </click-confirm>
          </div>
        </template>
      </b-table>
    </template>
    <template
      v-else
      class="
        w-100
        h-100
        d-flex
        align-items-center
        justify-content-center
        font-weight-bold
      "
    >
      <p>
        {{
          $t("list.no_data", { data: this.$t("menu.products").toLowerCase() })
        }}
      </p>
    </template>
  </div>
  <div v-else>
    <spinner />
  </div>
</template>

<script>
import proposalApi from '@/api/proposal'
import taxesApi from '@/api/taxes'
import model from '@/models/proposal-products'
import actions from '@/mixins/list'
import { mapGetters } from 'vuex'
import listTableHeader from '@/components/common/listTableHeader'
import {
  faArrowAltCircleUp,
  faEdit,
  faTrashAlt,
  faThumbsUp,
  faThumbsDown,
  faListAlt,
  faPlusSquare
} from '@fortawesome/free-regular-svg-icons'
import newProductForm from '@/components/budgets/proposal/newProductForm'
import ClickConfirm from 'click-confirm'

export default {
  components: {
    listTableHeader,
    newProductForm,
    ClickConfirm
  },
  props: {
    disableProposal: {
      type: Boolean,
      required: true
    }
  },
  watch: {
    products() {
      this.setTotal()
    }
  },
  data: () => ({
    proposalApi,
    taxesApi,
    taxes: [],
    products: [],
    editProduct: [],
    loading: true,
    showModal: false,
    fields: model.fields,
    filters: JSON.parse(JSON.stringify(model.filters)),
    faArrowAltCircleUp,
    faPlusSquare,
    faEdit,
    faTrashAlt,
    faThumbsUp,
    faThumbsDown,
    faListAlt
  }),
  created() {
    this.init()
  },
  computed: {
    ...mapGetters({
      items: 'form/relationshipFields'
    }),
    confirmationMessages() {
      return {
        title: this.$t('confirm.are_you_sure'),
        yes: this.$t('confirm.yes'),
        no: this.$t('confirm.no')
      }
    }
  },
  mixins: [actions],
  methods: {
    async init() {
      await Promise.all([this.updateVisibleFields(), this.getTaxes()])

      this.setProducts()

      this.loading = false
    },
    async getTaxes() {
      try {
        const response = await this.taxesApi.all()

        if (response && response.data && response.data.list) {
          this.taxes = response.data.list.map(item => ({
            id: item.id,
            label: this.formatPercentageWithSymbol(item.value)
          }))
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    formatProduct(product) {
      if (product.pivot) {
        return {
          ...product,
          price_unit: product.pivot.price_unit,
          price_package: product.pivot.price_package,
          product_id: product.id,
          position: product.pivot.position,
          batch: product.pivot.batch,
          entity_code: product.pivot.entity_code,
          client_code: product.pivot.client_code,
          quantity: product.pivot.quantity,
          quantity_package: product.pivot.quantity_package,
          quantity_per_package: product.quantity_per_package,
          designation:
            product.commercial_name +
            ' ' +
            product.dosage +
            ' ' +
            product.dosage_form,
          total_price: product.pivot.total_price,
          commercial_discount: product.pivot.commercial_discount,
          other_fields: product.pivot.other_fields,
          observations: product.pivot.observations
        }
      }
      return product
    },
    setProducts() {
      if (this.items.products) {
        this.products = this.items.products.map(product =>
          this.formatProduct(product)
        )
      } else {
        this.products = []
      }
    },
    formatPercentageWithSymbol(value) {
      return Math.round(value * Math.pow(10, 3) * 100) / Math.pow(10, 3) + '%'
    },
    openModal() {
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.editProduct = null
    },
    callEditItem(index) {
      this.getProduct(index)
      this.openModal()
    },
    async getProduct(i) {
      this.editProduct = this.products.filter((item, index) => index === i)
      const addIndex = this.editProduct.map(product => ({
        ...product,
        index: i
      }))
      this.editProduct = addIndex
    },
    addNewProduct(item) {
      this.products = [
        ...this.products,
        {
          ...item,
          ...{ proposal_id: this.items.id }
        }
      ]
      this.items.products = this.products
    },
    editSelectedProduct(item) {
      this.$set(this.products, item.index, item)
      this.products.map(product => {
        return (product.pivot = [])
      })
      this.items.products = this.products
    },
    removeSelectedProduct(i) {
      const newProductsList = this.products.filter((product, index) => {
        return index !== i
      })
      this.products = newProductsList
      this.items.products = this.products
    },
    getField(item, field) {
      if (item[field.field]) {
        if (
          typeof item[field.field] === 'string' ||
          typeof item[field.field] === 'number'
        ) {
          if (field.field === 'commercial_discount') {
            return item[field.field] * 100 + '%'
          } else {
            return item[field.field]
          }
        } else {
          return item[field.field]['name']
        }
      }
    },
    setTotal() {
      const total = this.products.reduce(
        (accum, item) => accum + Number(item.total_price),
        0
      )
      this.$emit('updateTotal', total.toFixed(2))
    }
  }
}
</script>
<style lang="scss" scoped>
.addBtn {
  right: 0;
  top: -2rem;
}
</style>